import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  CurrencyDollarIcon,
  PlusIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { obtenerTiposVenta, crearTipoVenta, crearVenta, editarVenta, usarArmazonStock, verificarYCrearAnteojo } from '../api/backend_v2';
import {
  DESCUENTO_EFECTIVO_DEFAULT,
  DESCUENTO_ARMAZONES_DEFAULT,
  cargarDescuentoEfectivoSeguro,
  cargarDescuentoArmazonesSeguro,
  resolverCondicionesArmazon,
  calcularPrecioEfectivoConDescuento,
  esArmazonCacic,
} from '../utils/precios';
import { obtenerProveedores } from '../utils/proveedores';
import ArmazonSearchInput from './ArmazonSearchInput';
import ProductoSearchInput from './ProductoSearchInput';
import ModalDuplicados from './ModalDuplicados';
import { isDemoUser, solicitarRegistroDemo } from '../utils/demoMode';
import { authService } from '../api/auth';

// Tipos cuyo nombre coincide con armazones
const TIPOS_ARMAZON = ['anteojos de sol', 'armazón', 'armazon'];
// Tipos cuyo nombre coincide con productos (fornituras)
const TIPOS_PRODUCTO = ['fornitura'];

const TIPOS_PAGO = [
  { value: 'efectivo',         label: 'Efectivo' },
  { value: 'tarjeta_credito',  label: 'Tarjeta de Crédito' },
  { value: 'tarjeta_debito',   label: 'Tarjeta de Débito' },
  { value: 'transferencia',    label: 'Transferencia' },
];

const FORM_INICIAL = {
  tipo_venta_id: '',
  tipo_venta_nombre: '',
  cliente_nombre: '',
  cliente_apellido: '',
  cliente_telefono: '',
  descripcion: '',
  monto: '',
  tipo_pago: 'efectivo',

};

const VentaModal = ({ onClose, onSuccess, clienteId = null, clienteDatos = null, venta = null }) => {
  const modoEdicion = !!venta;
  const [tipos, setTipos] = useState([]);
  const [form, setForm] = useState(FORM_INICIAL);
  const [agregandoTipo, setAgregandoTipo] = useState(false);
  const [nuevoTipo, setNuevoTipo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [exito, setExito] = useState(false);
  const [error, setError] = useState(null);
  const [clienteIdVinculado, setClienteIdVinculado] = useState(clienteId || venta?.cliente_id || null);

  // Modal de duplicados
  const [mostrarModalDuplicados, setMostrarModalDuplicados] = useState(false);
  const [clientesDuplicados, setClientesDuplicados] = useState([]);
  const [mensajeDuplicados, setMensajeDuplicados] = useState('');
  const [intentoCreacionEnCurso, setIntentoCreacionEnCurso] = useState(null);

  // Armazón vinculado (cuando tipo es anteojos de sol / armazón)
  const [armazonSeleccionado, setArmazonSeleccionado] = useState(null); // {id, marca, modelo, color}
  const [armazonDisplay, setArmazonDisplay] = useState('');

  // Producto vinculado (cuando tipo es fornituras)
  const [productoSeleccionado, setProductoSeleccionado] = useState(null); // {id, producto, tipo}
  const [productoDisplay, setProductoDisplay] = useState('');

  // Estados para condiciones comerciales
  const [descuentoOptica, setDescuentoOptica] = useState(DESCUENTO_EFECTIVO_DEFAULT);
  const [descuentoArmazon, setDescuentoArmazon] = useState(DESCUENTO_ARMAZONES_DEFAULT);
  const [proveedores, setProveedores] = useState([]);

  // Detección del tipo seleccionado
  const tipoLower = form.tipo_venta_nombre?.toLowerCase() || '';
  const mostrarBuscadorArmazon = TIPOS_ARMAZON.some((t) => tipoLower.includes(t));
  const mostrarBuscadorProducto = !mostrarBuscadorArmazon && TIPOS_PRODUCTO.some((t) => tipoLower.includes(t));

  // Si viene de ListaClientes con datos del cliente, prepopular los campos
  useEffect(() => {
    if (modoEdicion && venta) {
      // Modo edición: prepopular con datos de la venta
      setForm({
        tipo_venta_id: String(venta.tipo_venta_id || ''),
        tipo_venta_nombre: venta.tipo_venta_nombre || '',
        cliente_nombre: venta.cliente_nombre || '',
        cliente_apellido: venta.cliente_apellido || '',
        cliente_telefono: venta.cliente_telefono || '',
        descripcion: venta.descripcion || '',
        monto: String(venta.monto || ''),
        tipo_pago: venta.tipo_pago || 'efectivo',
      });
      
      // Cargar cliente vinculado si existe
      if (venta.cliente_id) {
        setClienteIdVinculado(venta.cliente_id);
      }
      
      // Cargar armazón si fue registrado en la venta
      if (venta.id_armazon && venta.armazon_marca) {
        setArmazonSeleccionado({
          id: venta.id_armazon,
          marca: venta.armazon_marca,
          modelo: venta.armazon_modelo,
          color: venta.armazon_color,
        });
        setArmazonDisplay(`${venta.armazon_marca} ${venta.armazon_modelo} - ${venta.armazon_color}`);
      }
    } else if (clienteId && clienteDatos) {
      // Modo creación con cliente predefinido
      setClienteIdVinculado(clienteId);
      setForm((f) => ({
        ...f,
        cliente_nombre: clienteDatos.nombre || '',
        cliente_apellido: clienteDatos.apellido || '',
        cliente_telefono: clienteDatos.telefono || '',
      }));
    }
  }, [modoEdicion, venta, clienteId, clienteDatos]);

  useEffect(() => {
    cargarTipos();
    cargarConfiguracionComercial();
  }, []);

  const cargarConfiguracionComercial = async () => {
    try {
      const [descEf, descArm, provs] = await Promise.all([
        cargarDescuentoEfectivoSeguro(),
        cargarDescuentoArmazonesSeguro(),
        obtenerProveedores().catch(() => []),
      ]);
      setDescuentoOptica(descEf);
      setDescuentoArmazon(descArm);
      setProveedores(Array.isArray(provs) ? provs : []);
    } catch (e) {
      console.warn('Error al cargar configuración comercial en VentaModal:', e);
    }
  };

  const cargarTipos = async () => {
    try {
      const data = await obtenerTiposVenta();
      setTipos(data.tipos || []);
    } catch (_) {
      // Non-fatal: el usuario puede escribir el tipo libremente
    }
  };

  // Calcular precio del armazón según método de pago
  const calcularPrecioArmazonPorPago = React.useCallback((armazon, tipoPago) => {
    if (!armazon) return '';
    const esEfectivo = tipoPago === 'efectivo' || tipoPago === 'transferencia';
    const precioPublico = armazon.precio_publico != null ? Number(armazon.precio_publico) : 0;

    if (!esEfectivo) {
      return precioPublico > 0 ? String(Math.round(precioPublico)) : '';
    }

    if (armazon.precio_efectivo != null && !isNaN(Number(armazon.precio_efectivo)) && Number(armazon.precio_efectivo) > 0) {
      return String(Math.round(Number(armazon.precio_efectivo)));
    }

    if (precioPublico > 0) {
      const cond = resolverCondicionesArmazon(armazon, proveedores, descuentoArmazon);
      const desc = cond.tieneCondicionEspecial
        ? cond.descuento
        : (esArmazonCacic(armazon) ? descuentoArmazon : (descuentoArmazon || descuentoOptica));
      const efCalc = calcularPrecioEfectivoConDescuento(precioPublico, desc);
      return efCalc ? String(Math.round(Number(efCalc))) : String(Math.round(precioPublico));
    }

    return '';
  }, [proveedores, descuentoArmazon, descuentoOptica]);

  // Calcular precio de producto según método de pago
  const calcularPrecioProductoPorPago = React.useCallback((prod, tipoPago) => {
    if (!prod) return '';
    const esEfectivo = tipoPago === 'efectivo' || tipoPago === 'transferencia';
    const precioLista = prod.precio_lista != null ? Number(prod.precio_lista) : 0;

    if (!esEfectivo) {
      return precioLista > 0 ? String(Math.round(precioLista)) : '';
    }

    if (prod.precio_efectivo != null && !isNaN(Number(prod.precio_efectivo)) && Number(prod.precio_efectivo) > 0) {
      return String(Math.round(Number(prod.precio_efectivo)));
    }

    return precioLista > 0 ? String(Math.round(precioLista)) : '';
  }, []);

  const handleSelectArmazon = (a) => {
    setArmazonSeleccionado(a);
    setArmazonDisplay(`${a.marca} ${a.modelo} - ${a.color}`);
    const montoCalculado = calcularPrecioArmazonPorPago(a, form.tipo_pago);
    if (montoCalculado) {
      setForm((f) => ({ ...f, monto: montoCalculado }));
    }
  };

  const handleSelectProducto = (p) => {
    setProductoSeleccionado(p);
    setProductoDisplay(p?.producto ?? '');
    const montoCalculado = calcularPrecioProductoPorPago(p, form.tipo_pago);
    if (montoCalculado) {
      setForm((f) => ({ ...f, monto: montoCalculado }));
    }
  };

  const handleTipoPagoChange = (e) => {
    const nuevoTipoPago = e.target.value;
    setForm((f) => {
      let nuevoMonto = f.monto;
      if (armazonSeleccionado) {
        const calc = calcularPrecioArmazonPorPago(armazonSeleccionado, nuevoTipoPago);
        if (calc) nuevoMonto = calc;
      } else if (productoSeleccionado) {
        const calc = calcularPrecioProductoPorPago(productoSeleccionado, nuevoTipoPago);
        if (calc) nuevoMonto = calc;
      }
      return { ...f, tipo_pago: nuevoTipoPago, monto: nuevoMonto };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleTipoChange = (e) => {
    const val = e.target.value;
    if (val === '__nuevo__') {
      setAgregandoTipo(true);
      setForm((f) => ({ ...f, tipo_venta_id: '', tipo_venta_nombre: '' }));
      // Resetear selecciones anteriores
      setArmazonSeleccionado(null); setArmazonDisplay('');
      setProductoSeleccionado(null); setProductoDisplay('');
      return;
    }
    const tipo = tipos.find((t) => String(t.id) === val);
    setForm((f) => ({
      ...f,
      tipo_venta_id: val,
      tipo_venta_nombre: tipo ? tipo.nombre : '',
    }));
    // Resetear selecciones si cambia el tipo
    setArmazonSeleccionado(null); setArmazonDisplay('');
    setProductoSeleccionado(null); setProductoDisplay('');
  };

  const guardarNuevoTipo = async () => {
    if (!nuevoTipo.trim()) return;
    try {
      const data = await crearTipoVenta({ nombre: nuevoTipo.trim() });
      await cargarTipos();
      setForm((f) => ({
        ...f,
        tipo_venta_id: String(data.id),
        tipo_venta_nombre: nuevoTipo.trim(),
      }));
      setAgregandoTipo(false);
      setNuevoTipo('');
    } catch (_) {
      setAgregandoTipo(false);
      setNuevoTipo('');
    }
  };

  // Calcular estilos de similitud
  const getEstiloSimilitud = (similitud) => {
    if (similitud >= 0.90) return 'bg-red-100 text-red-700';
    if (similitud >= 0.75) return 'bg-yellow-100 text-yellow-700';
    return 'bg-blue-100 text-blue-700';
  };

  // Helper: construir datos de venta
  const _construirVentaData = (clienteIdParam = null) => {
    const nombreArmazonSeleccionado = armazonSeleccionado
      ? `${armazonSeleccionado.marca} ${armazonSeleccionado.modelo} ${armazonSeleccionado.color || ''}`.trim()
      : null;

    const nombreArmazonManual = armazonDisplay?.trim() || null;

    return {
      tipo_venta_id: form.tipo_venta_id ? Number.parseInt(form.tipo_venta_id, 10) : null,
      tipo_venta_nombre: form.tipo_venta_nombre || null,
      cliente_id: clienteIdParam ?? clienteIdVinculado,
      cliente_nombre: form.cliente_nombre.trim() || null,
      descripcion: form.descripcion.trim() || null,
      monto: parseFloat(form.monto),
      tipo_pago: form.tipo_pago,
      id_armazon: armazonSeleccionado?.id ?? null,
      nombre_armazon: nombreArmazonSeleccionado || nombreArmazonManual || null,
      id_producto: productoSeleccionado?.id ?? null,
    };
  };

  // Helper: descontar stock
  const _descontarStock = async () => {
    if (armazonSeleccionado?.id) {
      try {
        await usarArmazonStock(armazonSeleccionado.id, 1);
      } catch (stockError) {
        console.warn('Advertencia: No se pudo descontar stock:', stockError.message);
      }
    }
  };

  // Helper: procesar éxito
  const _procesarExito = (clienteIdNuevo = null) => {
    setExito(true);
    setTimeout(() => {
      onClose();
      onSuccess?.(clienteIdNuevo ? { cliente_id: clienteIdNuevo } : undefined);
    }, 1200);
  };

  // Handler: editar venta existente
  const _handleEditarVenta = async () => {
    const ventaData = _construirVentaData();
    await editarVenta(venta.id, ventaData);
    _procesarExito();
  };

  // Handler: crear venta sin cliente
  const _handleCrearVentaSinCliente = async () => {
    const ventaData = _construirVentaData(null);
    ventaData.cliente_nombre = null;
    await crearVenta(ventaData);
    await _descontarStock();
    _procesarExito();
  };

  // Handler: crear venta con cliente (con verificación de duplicados)
  const _handleCrearVentaConCliente = async () => {
    const tieneNombre = form.cliente_nombre.trim();
    const tieneApellido = form.cliente_apellido.trim();
    const tieneTelefono = form.cliente_telefono.trim();

    if (!tieneNombre || !tieneApellido || !tieneTelefono) {
      setError('Para guardar un cliente, ingresá nombre, apellido y teléfono.');
      return;
    }

    const datosCliente = {
      nombre: tieneNombre,
      apellido: tieneApellido,
      telefono: tieneTelefono,
    };

    const resultado = await verificarYCrearAnteojo(datosCliente, {}, clienteIdVinculado, false);

    if (resultado.duplicado_encontrado && !clienteIdVinculado) {
      setClientesDuplicados(resultado.opciones[0]?.clientes || []);
      setMensajeDuplicados(resultado.mensaje);
      setIntentoCreacionEnCurso({
        datosCliente,
        anteojoData: {},
        ventaData: _construirVentaData(null),
      });
      setMostrarModalDuplicados(true);
      return;
    }

    if (resultado.exito) {
      const nuevoClienteId = resultado.cliente_id;
      const ventaData = _construirVentaData(nuevoClienteId);
      await crearVenta(ventaData);
      await _descontarStock();
      _procesarExito(nuevoClienteId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isDemoUser(authService.obtenerUsuarioActual())) {
      onClose();
      solicitarRegistroDemo('registrar tus propias ventas y arqueo de caja');
      return;
    }

    setError(null);

    const monto = parseFloat(form.monto);
    if (!form.monto || isNaN(monto) || monto <= 0) {
      setError('Ingresá un monto mayor a 0.');
      return;
    }

    if (armazonSeleccionado?.id && (armazonSeleccionado.cantidad || 0) <= 0) {
      setError('No hay stock disponible del armazón seleccionado.');
      return;
    }

    try {
      setGuardando(true);

      if (modoEdicion) {
        await _handleEditarVenta();
      } else {
        const tieneNombre = form.cliente_nombre.trim();
        const tieneApellido = form.cliente_apellido.trim();
        const tieneTelefono = form.cliente_telefono.trim();
        const tieneDatos = tieneNombre && tieneApellido && tieneTelefono;

        if (!tieneDatos) {
          await _handleCrearVentaSinCliente();
        } else {
          await _handleCrearVentaConCliente();
        }
      }
    } catch (e) {
      setError(e.message || 'Error al registrar la venta.');
      setGuardando(false);
    } finally {
      setGuardando(false);
    }
  };

  // Manejar selección de cliente existente
  const handleSeleccionarClienteExistente = async (clienteSeleccionado) => {
    if (!intentoCreacionEnCurso) return;

    try {
      setGuardando(true);
      setMostrarModalDuplicados(false);

      const ventaData = {
        ...intentoCreacionEnCurso.ventaData,
        cliente_id: clienteSeleccionado.id,
      };

      await crearVenta(ventaData);
      await _descontarStock();
      _procesarExito(clienteSeleccionado.id);
    } catch (e) {
      setError(e.message || 'Error al registrar la venta.');
      setGuardando(false);
    }
  };

  // Manejar confirmación de crear cliente nuevo
  const handleConfirmarCrearNuevo = async () => {
    if (!intentoCreacionEnCurso) return;

    try {
      setGuardando(true);
      setMostrarModalDuplicados(false);

      const resultado = await verificarYCrearAnteojo(
        intentoCreacionEnCurso.datosCliente,
        intentoCreacionEnCurso.anteojoData,
        null,
        true
      );

      if (resultado.exito) {
        const nuevoClienteId = resultado.cliente_id;
        const ventaData = {
          ...intentoCreacionEnCurso.ventaData,
          cliente_id: nuevoClienteId,
        };

        await crearVenta(ventaData);
        await _descontarStock();
        _procesarExito(nuevoClienteId);
      }
    } catch (e) {
      setError(e.message || 'Error al registrar la venta.');
      setGuardando(false);
    }
  };

  // Helper: renderizar banner de error
  const _renderErrorBanner = () => {
    if (!error) return null;
    return (
      <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm">
        {error}
      </div>
    );
  };

  // Helper: determinar si mostrar formulario
  const _showFormulario = () => !exito;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ zIndex: 2147483646 }}
    >
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto">

        {/* Header */}
        <div className="sticky top-0 bg-white flex items-center justify-between px-4 py-3 border-b border-gray-100 rounded-t-2xl z-10">
          <div className="flex items-center gap-2">
            <div className="bg-green-100 p-2 rounded-lg">
              <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
            </div>
            <h2 className="text-base font-bold text-gray-800">
              {modoEdicion ? 'Editar Venta' : 'Nueva Venta'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Cerrar"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {exito && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <CheckCircleIcon className="h-14 w-14 text-green-500" />
            <p className="text-green-700 font-semibold text-lg">¡Venta registrada!</p>
          </div>
        )}

        {_showFormulario() && (
          <form onSubmit={handleSubmit} className="px-4 py-4 space-y-4">
            {_renderErrorBanner()}

            {/* Tipo de venta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de venta
              </label>
              {agregandoTipo ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={nuevoTipo}
                    onChange={(e) => setNuevoTipo(e.target.value)}
                    placeholder="Nombre del nuevo tipo"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-400 focus:border-transparent"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); guardarNuevoTipo(); } }}
                  />
                  <button
                    type="button"
                    onClick={guardarNuevoTipo}
                    className="bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    OK
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAgregandoTipo(false); setNuevoTipo(''); }}
                    className="text-gray-400 hover:text-gray-600 px-2"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <select
                  name="tipo_venta_id"
                  value={form.tipo_venta_id}
                  onChange={handleTipoChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-400 focus:border-transparent"
                >
                  <option value="">— Seleccionar tipo —</option>
                  {tipos.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nombre}{t.es_personalizado ? ' ✎' : ''}
                    </option>
                  ))}
                  <option value="__nuevo__">+ Agregar tipo nuevo...</option>
                </select>
              )}
            </div>

            {/* Buscador de armazón (para "Anteojos de sol" / "Armazón") */}
            {mostrarBuscadorArmazon && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Armazón vinculado
                  <span className="text-gray-400 font-normal text-xs ml-1">(opcional)</span>
                </label>
                <ArmazonSearchInput
                  name="armazon_display"
                  value={armazonDisplay}
                  onChange={(e) => {
                    setArmazonDisplay(e.target.value);
                    if (!e.target.value) {
                      setArmazonSeleccionado(null);
                    }
                  }}
                  onArmazonSelect={handleSelectArmazon}
                  placeholder="Buscar armazón..."
                />
                {armazonSeleccionado?.id && (
                  <div className="mt-2 flex items-center gap-2">
                    <p className="text-xs text-green-600 font-medium">
                      ✓ Armazón #{armazonSeleccionado.id} seleccionado
                    </p>
                    {armazonSeleccionado?.cantidad !== undefined && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                        (armazonSeleccionado.cantidad || 0) > 0
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {(armazonSeleccionado.cantidad || 0) > 0 
                          ? `${armazonSeleccionado.cantidad} en stock`
                          : 'Sin stock'}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Buscador de producto (para "Fornituras") */}
            {mostrarBuscadorProducto && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Producto (fornitura)
                  <span className="text-gray-400 font-normal text-xs ml-1">(opcional)</span>
                </label>
                <ProductoSearchInput
                  name="producto_display"
                  value={productoDisplay}
                  onChange={(e) => {
                    setProductoDisplay(e.target.value);
                    if (!e.target.value) {
                      setProductoSeleccionado(null);
                    }
                  }}
                  onProductoSelect={handleSelectProducto}
                  placeholder="Buscar o crear fornitura..."
                  tipoDefault="Fornituras"
                />
                {productoSeleccionado?.id && (
                  <p className="mt-1 text-xs text-green-600 font-medium">
                    ✓ Producto #{productoSeleccionado.id} seleccionado
                  </p>
                )}
              </div>
            )}

            {/* Cliente (opcional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cliente <span className="text-gray-400 font-normal text-xs">(opcional)</span>
              </label>
              <div className="space-y-2">
                <input
                  type="text"
                  name="cliente_nombre"
                  value={form.cliente_nombre}
                  onChange={handleChange}
                  placeholder="Nombre"
                  disabled={!!clienteIdVinculado}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <input
                  type="text"
                  name="cliente_apellido"
                  value={form.cliente_apellido}
                  onChange={handleChange}
                  placeholder="Apellido"
                  disabled={!!clienteIdVinculado}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <input
                  type="text"
                  name="cliente_telefono"
                  value={form.cliente_telefono}
                  onChange={handleChange}
                  placeholder="Teléfono"
                  disabled={!!clienteIdVinculado}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {clienteIdVinculado && (
                  <p className="text-xs text-green-600 font-medium">
                    ✓ Cliente vinculado (ID: {clienteIdVinculado})
                  </p>
                )}
              </div>
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción <span className="text-gray-400 font-normal text-xs">(opcional)</span>
              </label>
              <input
                type="text"
                name="descripcion"
                value={form.descripcion}
                onChange={handleChange}
                placeholder="Ej: Limpiador 60ml, reparación bisagra..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-400 focus:border-transparent"
              />
            </div>

            {/* Monto y Forma de pago */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Monto *
                  </label>
                  {(armazonSeleccionado || productoSeleccionado) && (
                    <span className="text-[11px] text-gray-400">
                      Editable
                    </span>
                  )}
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium pointer-events-none">
                    $
                  </span>
                  <input
                    type="number"
                    name="monto"
                    value={form.monto}
                    onChange={handleChange}
                    min="0.01"
                    step="0.01"
                    required
                    placeholder="0"
                    className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2 text-sm focus:ring-2 focus:ring-green-400 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Forma de pago *
                </label>
                <select
                  name="tipo_pago"
                  value={form.tipo_pago}
                  onChange={handleTipoPagoChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-400 focus:border-transparent"
                >
                  {TIPOS_PAGO.map((tp) => (
                    <option key={tp.value} value={tp.value}>{tp.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Chips de sugerencia rápida de precio según método de pago */}
            {armazonSeleccionado && (
              <div className="flex flex-wrap items-center gap-2 pt-0.5">
                <span className="text-xs text-gray-500 font-medium">Precios de stock:</span>
                {calcularPrecioArmazonPorPago(armazonSeleccionado, 'efectivo') && (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, monto: calcularPrecioArmazonPorPago(armazonSeleccionado, 'efectivo') }))}
                    title="Aplicar precio efectivo / transferencia"
                    className={`px-2 py-0.5 rounded text-xs transition-colors border ${
                      form.monto === calcularPrecioArmazonPorPago(armazonSeleccionado, 'efectivo')
                        ? 'bg-green-100 border-green-300 text-green-800 font-semibold'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Efectivo: ${Number(calcularPrecioArmazonPorPago(armazonSeleccionado, 'efectivo')).toLocaleString('es-AR')}
                  </button>
                )}
                {calcularPrecioArmazonPorPago(armazonSeleccionado, 'tarjeta_credito') && (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, monto: calcularPrecioArmazonPorPago(armazonSeleccionado, 'tarjeta_credito') }))}
                    title="Aplicar precio lista / tarjeta"
                    className={`px-2 py-0.5 rounded text-xs transition-colors border ${
                      form.monto === calcularPrecioArmazonPorPago(armazonSeleccionado, 'tarjeta_credito')
                        ? 'bg-blue-100 border-blue-300 text-blue-800 font-semibold'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Tarjeta: ${Number(calcularPrecioArmazonPorPago(armazonSeleccionado, 'tarjeta_credito')).toLocaleString('es-AR')}
                  </button>
                )}
              </div>
            )}

            {/* Botón submit */}
            <button
              type="submit"
              disabled={guardando}
              className="w-full bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white font-semibold py-3 rounded-xl text-sm transition-colors mt-2"
            >
              {guardando ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  {modoEdicion ? 'Actualizando...' : 'Registrando...'}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <PlusIcon className="h-4 w-4" />
                  {modoEdicion ? 'Actualizar Venta' : 'Registrar Venta'}
                </span>
              )}
            </button>
          </form>
        )}

        {/* Modal de duplicados */}
        <ModalDuplicados
          mostrar={mostrarModalDuplicados}
          clientes={clientesDuplicados}
          mensaje={mensajeDuplicados}
          onSeleccionar={handleSeleccionarClienteExistente}
          onConfirmarNuevo={handleConfirmarCrearNuevo}
          guardando={guardando}
          getEstiloSimilitud={getEstiloSimilitud}
        />
      </div>
    </div>
  );
};

export default VentaModal;
